/**
 * One reading of "when did this happen", for values that came back from Firestore.
 *
 * A `Date` written into Firestore is read back as a `Timestamp`, and
 * `new Date(timestamp)` yields `Invalid Date` — which sorts unpredictably and
 * renders to the user as the literal string "Invalid Date". Four stores had
 * each grown their own guard against that; the notification panel had none, and
 * showed exactly that string on every row ([[BUGS #156]]).
 *
 * So the guard lives in one place now. New readers use this rather than writing
 * a fifth copy.
 */
import { Timestamp } from 'firebase/firestore';

/**
 * Coerce a Firestore `Timestamp`, an ISO string, a millisecond number or a
 * `Date` into a `Date`.
 *
 * Anything unreadable returns `fallback` (epoch by default) rather than an
 * `Invalid Date`: a row that groups under "Earlier" is wrong in a way a reader
 * can ignore, and one that renders "Invalid Date" is not.
 */
export function toDate(value: unknown, fallback: Date = new Date(0)): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return isNaN(value.getTime()) ? fallback : value;
  // A plain object carrying `toDate` — what a Timestamp looks like once it has
  // crossed a serialization boundary that stripped its prototype.
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const converted = (value as { toDate: () => Date }).toDate();
    return converted instanceof Date && !isNaN(converted.getTime()) ? converted : fallback;
  }
  // `{seconds, nanoseconds}` — the same thing after a JSON round-trip.
  const seconds = (value as { seconds?: unknown } | null)?.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return fallback;
}

export default toDate;
